import {
  Injectable,
  Inject,
  makeStateKey,
  Renderer2,
  RendererFactory2,
  PLATFORM_ID,
} from "@angular/core";
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from "@angular/common"; // FIX: Imported isPlatformServer
import {
  Resolve,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";
import { GeneralService } from "app/services/general.service";
import { Machine } from "app/shared/machine";
import { Meta, TransferState, Title } from "@angular/platform-browser";
import { Observable, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators"; // IMPROVEMENT: Imported tap operator for cleaner side-effects

@Injectable({
  providedIn: "root",
})
export class MachineResolver implements Resolve<Machine> {
  private renderer: Renderer2;
  private MACHINE_KEY = makeStateKey<Machine>("machine");

  constructor(
    private machineService: GeneralService,
    private meta: Meta,
    private title: Title,
    private transferState: TransferState,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<Machine> {
    const lang = route.paramMap.get("lang");
    const machineId = route.paramMap.get("id");

    // On the server, always fetch fresh data.
    if (isPlatformServer(this.platformId)) {
      return this.fetchMachineData(machineId, lang);
    }

    // On the browser, check TransferState first.
    if (this.transferState.hasKey(this.MACHINE_KEY)) {
      const machineData = this.transferState.get<Machine>(
        this.MACHINE_KEY,
        null
      );
      // Ensure the cached data is for the correct machine before using it.
      if (machineData && machineId === machineData._id) {
        this.transferState.remove(this.MACHINE_KEY); // Clean up the state
        this.updateMetaTags(machineData, lang);
        return of(machineData);
      }
    }

    // Otherwise, fetch from the API on the browser.
    return this.fetchMachineData(machineId, lang);
  }

  private fetchMachineData(
    machineId: string,
    lang: string
  ): Observable<Machine> {
    return this.machineService
      .getItem("machine/getMachineByName", machineId)
      .pipe(
        tap((machineData) => {
          // If on the server, set the data in TransferState for the browser to use.
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(this.MACHINE_KEY, machineData);
          }
          this.updateMetaTags(machineData, lang);
        })
      );
  }

  /**
   * IMPROVEMENT: Helper function to strip HTML tags from a string.
   */
  private stripHtml(html: string): string {
    if (!html) return "";
    // On the browser, we can use the DOM parser for a robust solution.
    if (isPlatformBrowser(this.platformId)) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent || "";
    }
    // On the server, a simple regex is safer and avoids DOM dependencies.
    return html
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  private updateMetaTags(machineData: Machine, lang: string): void {
    if (!machineData) {
      return;
    }

    try {
      // --- METAS ---
      const metaTitle = machineData.metaTag?.[lang] || machineData.name?.[lang];
      // FIX: Strip HTML from description before setting it.
      const metaDescription = this.stripHtml(
        machineData.metaDescription?.[lang] || machineData.description?.[lang]
      );

      this.title.setTitle(metaTitle);
      this.meta.updateTag({
        property: "og:title",
        content: metaTitle,
      });

      // FIX: Use `name="description"` for the standard meta description tag.
      this.meta.updateTag({
        name: "description",
        content: metaDescription,
      });

      this.meta.updateTag({
        property: "og:description",
        content: metaDescription,
      });

      // IMPROVEMENT: Safer access to the image URL.
      if (machineData.images?.[0]?.url) {
        this.meta.updateTag({
          property: "og:image",
          content: machineData.images[0].url,
        });
      }

      // --- JSON-LD VIDEO SCHEMA ---
      if (machineData.youTubeVideos && machineData.youTubeVideos.length > 0) {
        const firstVideo = machineData.youTubeVideos[0];
        const embedUrl = `https://www.youtube.com/embed/${firstVideo.url}`;
        const youTubeThumbnailUrl = `https://img.youtube.com/vi/${firstVideo.url}/hqdefault.jpg`;

        this.addVideoSchema({
          name: machineData.name[lang],
          description: metaDescription, // Use the cleaned description
          thumbnailUrl: youTubeThumbnailUrl,
          embedUrl: embedUrl,
          uploadDate: machineData.createdAt,
          // IMPROVEMENT: Highlight that this should be dynamic.
          duration: "PT0H9M51S", // TODO: Make this duration dynamic if possible.
          regionsAllowed: ["IN"], // TODO: Make regions dynamic if they vary.
        });
      }
    } catch (error) {
      console.error("Error updating meta tags:", error);
    }
  }

  private addVideoSchema(opts: {
    name: string;
    description: string;
    thumbnailUrl: string;
    embedUrl: string;
    uploadDate: string;
    duration: string;
    regionsAllowed: string[];
  }) {
    // FIX: The script MUST be rendered on the server for SEO.
    // It should also be rendered on the client for consistency during navigation.
    const scriptId = "video-schema";
    const videoObjectJson = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: opts.name,
      description: opts.description,
      thumbnailUrl: [opts.thumbnailUrl],
      uploadDate: opts.uploadDate,
      duration: opts.duration,
      embedUrl: opts.embedUrl,
      // IMPROVEMENT: Simplified regionsAllowed structure.
      regionsAllowed: opts.regionsAllowed,
    };

    // Remove existing script if it exists to avoid duplication during client-side navigation.
    const existingScript = this.document.getElementById(scriptId);
    if (existingScript) {
      this.renderer.removeChild(this.document.head, existingScript);
    }

    const script = this.renderer.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.text = JSON.stringify(videoObjectJson, null, 2); // Use indentation for readability in dev tools

    this.renderer.appendChild(this.document.head, script);
  }
}
